import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApplicationQuestionAndAnswerDto, CustomQuestionTypeEnum, CustomQuestionResponseTypeEnum, UpsertApplicantMinorV2Dto, ApplicationQuestionResponseV2Dto } from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import { convertSqlDateToUSCalendarDate, convertUSCalendarDateToSqlDate } from '../../../shared/util/converters';
import { InosioDateFormat } from '../../../shared/util/formaters';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { getMinorRecords, selectApplicantsState, updateCustomQuestionsAnswersV2, updateMinorRecord } from '../sagas/applicants/applicantsSlice';
import { DobRegex } from '../../../utils/regex';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import CustomQuestionsSection from './CustomQuestionsSection';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { compareAsc, subYears } from 'date-fns';

interface PropsType {}

const schemaValidationApplicationOccupant = Yup.object({
  FirstName: Yup.string().max(100, 'Max length is 100 characters').required('Please enter a first Name'),
  LastName: Yup.string().max(100, 'Max length is 100 characters').required('Please enter a last name'),
  MiddleName: Yup.string().max(100, 'Max length is 100 characters').nullable(),
  Dob: Yup.string()
    .min(10, 'Please enter a date in this format MM/DD/YYYY')
    .max(10, 'Please enter a date in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
    .test({
      name: 'Minor occupant age',
      exclusive: true,
      message: 'Minor occupants must be younger than 18 years old.',
      test: (value, testContext) => {
        if (testContext.parent.Dob) {
          const minorsAge = new Date(testContext.parent.Dob);
          const _18yrsAgoToday = subYears(new Date(), 18);

          const isYoungerThan18 = compareAsc(minorsAge, _18yrsAgoToday);
          return isYoungerThan18 >-1;
        }
        return false;
      },
    })
    .nullable(),
});

type schemaValidationApplicationOccupantType = Yup.InferType<typeof schemaValidationApplicationOccupant>;

export default function ApplicationMinor(props: PropsType) {
  const query = useQuery();
  const { id: minorUuidParam } = useParams<{ id: string }>();
  const hasValidationQueryParam = !!query.get('validation');
  const classes = applicationStyles();
  const dispatch = useDispatch();

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);
  const {
    Application,
    MinorRecords,
    isFetchingMinorRecordsFailed,
    CustomQuestionAnswers,
    isFetchingMinorRecordsSuccess,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
  } = useSelector(selectApplicantsState);

  //local states
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [currentMinorUuidParam] = useState(minorUuidParam);
  const [localError, setLocalError] = useState<string | null>(null);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const bottomRef = useRef<HTMLElement>(null);
  const [initialFormValues, setInitialFormValues] = useState<schemaValidationApplicationOccupantType>({
    FirstName: '',
    LastName: '',
    MiddleName: '',
    Dob: '',
  });

  //#region  v2 stuff
  //fetch employment/income records
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && !isFetchingMinorRecordsSuccess) {
      //they must have got here via direct link or via refresh.
      dispatch(getMinorRecords(ipaUser?.currentProfile.uuid));
    } else if (ipaUser?.currentProfile.uuid && isFetchingMinorRecordsSuccess) {
      const thisMinorRecord = MinorRecords?.find((e) => e.Uuid === currentMinorUuidParam);
      if (thisMinorRecord) {
        setInitialFormValues({
          ...initialFormValues,
          FirstName: thisMinorRecord.FirstName ?? '',
          LastName: thisMinorRecord.LastName ?? '',
          MiddleName: thisMinorRecord.MiddleName ?? '',
          Dob: thisMinorRecord.Dob ? convertSqlDateToUSCalendarDate(thisMinorRecord.Dob) : '',
        });
      } else {
        //bad
      }
    }
  }, [ipaUser, currentMinorUuidParam, isFetchingMinorRecordsSuccess]);

  //populate custom questions once prop group details received
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationEmployeeCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.AdditionalOccupantV1,
      );
      setQuestionsAndAnswers(
        organizationEmployeeCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === currentMinorUuidParam);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: previouslySavedResponseToThisQuestion?.ApplicantUuid!,
            BelongsToEntityUuid: currentMinorUuidParam,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, isFetchingCustomQuestionAnswersSuccess]);

  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingMinorRecordsSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingMinorRecordsFailed || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingMinorRecordsSuccess,
    isFetchingMinorRecordsFailed,
  ]);
  const handleFormValuesChanged = (e: any) => {
    /*
     */

    const payload = {
      FirstName: !formik.errors.FirstName ? formik.values.FirstName : undefined,
      MiddleName: !formik.errors.MiddleName ? formik.values.MiddleName || undefined : undefined,
      LastName: !formik.errors.LastName ? formik.values.LastName : undefined,
      Dob: !formik.errors.Dob ? (formik.values.Dob ? convertUSCalendarDateToSqlDate(formik.values.Dob) : undefined) : undefined,
    } as UpsertApplicantMinorV2Dto;

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateMinorRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        minorUuid: currentMinorUuidParam,
        updateDto: payload,
      },
    });
  };

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = questionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: minorUuidParam,
            ApplicantUuid: ipaUser?.currentProfile.uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setQuestionsAndAnswers(
      questionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  //#endregion v2 stuff

  const formik = useFormik<schemaValidationApplicationOccupantType>({
    initialValues: initialFormValues,
    validationSchema: schemaValidationApplicationOccupant,
    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  const formatDob = (dob: string) => {
    const formattedDob = InosioDateFormat(dob);
    formik.setFieldValue('Dob', formattedDob);
  };

  return (
    <Box sx={{ width: '100%', p: 2, pb: 4 }}>
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {localError && (
            <Grid item xs={12}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} lg={4.5} xl={3.5}>
            <Typography sx={{ mb: 2 }} className={classes.labelText}>
              Non-adult occupants
            </Typography>
            <Typography sx={{ ml: 1, mb: 2 }} className={classes.bodyText}>
              This is for occupants under 18 years of age.
            </Typography>
            <InputField
              label={'First Name'}
              isError={!!formik.errors?.FirstName && hasValidationQueryParam}
              errors={formik.errors?.FirstName}
              touched={formik.touched?.FirstName}
              {...formik.getFieldProps('FirstName')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
              showAsterisk={!formik.values.FirstName}
            />
            <InputField
              label={'Middle Name'}
              isError={false}
              errors={formik.errors?.MiddleName}
              touched={formik.touched?.MiddleName}
              {...formik.getFieldProps('MiddleName')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
            />
            <InputField
              label={'Last Name'}
              isError={!!formik.errors?.LastName && hasValidationQueryParam}
              errors={formik.errors?.LastName}
              touched={formik.touched?.LastName}
              showAsterisk={!formik.values.LastName}
              {...formik.getFieldProps('LastName')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
            />
            {propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsCollectNonAdultOccupantDob && (
              <InputField
                label={'Date of Birth'}
                placeholder="MM/DD/YYYY"
                isError={!!formik.errors?.Dob && hasValidationQueryParam}
                errors={formik.errors?.Dob}
                touched={formik.touched?.Dob}
                {...formik.getFieldProps('Dob')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                onChange={(e) => formatDob(e.target.value)}
                showAsterisk={!formik.values.Dob}
              />
            )}
          </Grid>
          <CustomQuestionsSection
            questionsAndAnswers={questionsAndAnswers}
            hasValidationQueryParam={hasValidationQueryParam}
            onFieldChange={handleCustomQuestionFieldChanged}
            onFieldUpdate={handleSaveCustomQuestionAnswer}
            bottomRef={bottomRef}
          />
        </Grid>
      )}
    </Box>
  );
}
